import React, { useState, useRef, useEffect } from 'react';
import { Camera, X, Video, ShoppingBag, Plus, Percent, Truck, AlertTriangle, Info, Gift } from 'lucide-react';
import { createLiveStream, getSellerProducts } from '@/services/liveStreamService';
import ProductCreateModal from '@/modals/ProductCreateModal';

interface Product {
  $id: string;
  title: string;
  price: string;
  description: string;
  images: string[];
  sellerId: string;
  status?: string;
  liveStreams?: string[];
}

interface LiveStreamCreatorProps {
  userId: string;
  userName: string;
  onStartStream: (streamId: string, stream: MediaStream) => void;
}

const LiveStreamCreator: React.FC<LiveStreamCreatorProps> = ({ userId, userName, onStartStream }) => {
  const [showProductModal, setShowProductModal] = useState(false);
  const [preparing, setPreparing] = useState(true);
  const [streamTitle, setStreamTitle] = useState('');
  const [description, setDescription] = useState('');
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDiscountSection, setShowDiscountSection] = useState(false);
  const [discount, setDiscount] = useState({
    enabled: false,
    percent: '',
    maxUsers: '',
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getSellerProducts(userId);
        setAvailableProducts(response as Product[]);
      } catch (error) {
        console.error("Failed to fetch products:", error);
        setAvailableProducts([]);
      }
    };
    fetchProducts();
  }, [userId]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      if (videoRef.current) videoRef.current.srcObject = stream;
      streamRef.current = stream;
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("Camera/microphone access denied. Please check permissions.");
    }
  };

  useEffect(() => {
    if (preparing) startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [preparing]);

  const handleProductCreated = (newProduct: Product) => {
    // Create a proper Product object from the response
    const productToAdd: Product = {
      $id: newProduct.$id || Date.now().toString(),
      title: newProduct.title,
      price: newProduct.price,
      description: newProduct.description,
      images: newProduct.images || [],
      sellerId: newProduct.sellerId,
      status: newProduct.status || "active",
      liveStreams: newProduct.liveStreams || []
    };
    
    setAvailableProducts(prev => [...prev, productToAdd]);
    setShowProductModal(false);
  };

  const addProduct = (product: Product) => {
    setFeaturedProducts(prev => {
      const newState = [...prev, product];
      return newState;
    });
  };

  useEffect(() => {
    // Track state changes
  }, [featuredProducts]);

  const removeProduct = (productId: string) => {
    setFeaturedProducts(prev => prev.filter(p => p.$id !== productId));
  };

  const calculateDiscountValue = () => {
    if (!discount.enabled || !discount.percent || featuredProducts.length === 0) {
      return 0;
    }
    
    const averagePrice = featuredProducts.reduce((sum, product) => sum + parseFloat(product.price), 0) / featuredProducts.length;
    return (averagePrice * parseFloat(discount.percent)) / 100;
  };

  const handleStartStream = async () => {
    if (!streamTitle.trim()) {
      alert("Please provide a stream title");
      return;
    }

    if (!streamRef.current) {
      alert("No stream available");
      return;
    }

    if (featuredProducts.length === 0) {
      alert("Please add at least one product to showcase");
      return;
    }

    setIsLoading(true);
    try {
      // Prepare live discount data
      const liveDiscount = discount.enabled && discount.percent && discount.maxUsers ? {
        percent: parseFloat(discount.percent),
        maxUsers: parseInt(discount.maxUsers),
      } : undefined;

      const response = await createLiveStream({
        title: streamTitle,
        description,
        sellerId: userId,
        sellerName: userName,
        featuredProducts: featuredProducts.map(p => p.$id),
        thumbnailFile: featuredProducts[0]?.images,
        liveDiscount // Add live discount to the stream
      });
      
      if (response.$id) {
        onStartStream(response.$id, streamRef.current);
      } else {
        throw new Error('Failed to create stream');
      }
    } catch (error) {
      console.error("Error starting stream:", error);
      alert("Failed to start stream. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col bg-gray-100 rounded-lg p-4 max-w-4xl mx-auto">
      <h2 className="text-xl text-gray-700 font-bold mb-4">Create Live Shopping Stream</h2>
      
      {preparing ? (
        <div className="space-y-4">
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            <video 
              ref={videoRef} 
              autoPlay 
              muted 
              playsInline
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-4 right-4 flex space-x-2">
              <button className="bg-red hover:bg-orange-500 text-white p-2 rounded-full">
                <Camera size={20} />
              </button>
              <button className="bg-gray-800 hover:bg-gray-900 text-white p-2 rounded-full">
                <Video size={20} />
              </button>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stream Title</label>
              <input
                type="text"
                value={streamTitle}
                onChange={(e) => setStreamTitle(e.target.value)}
                className="w-full text-gray-700 p-2 border border-gray-300 rounded-md"
                placeholder="Enter a catchy title for your stream"
                maxLength={100}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full text-gray-700 p-2 border border-gray-300 rounded-md"
                placeholder="Describe what you'll be showcasing in this stream"
                rows={3}
                maxLength={500}
              />
            </div>

            {/* Live Discount Section */}
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center text-sm font-medium text-gray-700">
                  <Gift className="w-4 h-4 mr-2 text-orange-600" />
                  Live Stream Discount (Optional)
                </label>
                <button
                  type="button"
                  onClick={() => setShowDiscountSection(!showDiscountSection)}
                  className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                >
                  {showDiscountSection ? 'Hide' : 'Add Discount'}
                </button>
              </div>
              
              {showDiscountSection && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="enableDiscount"
                      checked={discount.enabled}
                      onChange={(e) => setDiscount(prev => ({ ...prev, enabled: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="enableDiscount" className="text-sm text-gray-700">
                      Enable special discount for early buyers during live stream
                    </label>
                  </div>
                  
                  {discount.enabled && (
                    <div className="grid text-gray-700 grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">
                          Discount Percentage
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            placeholder="e.g., 20"
                            min="1"
                            max="50"
                            value={discount.percent}
                            onChange={(e) => setDiscount(prev => ({ ...prev, percent: e.target.value }))}
                            className="w-full p-2 pr-8 border border-gray-300 rounded-md text-center font-semibold"
                          />
                          <Percent size={14} className="absolute right-2 top-2.5 text-gray-400" />
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">
                          Max Early Buyers
                        </label>
                        <input
                          type="number"
                          placeholder="e.g., 10"
                          min="1"
                          max="100"
                          value={discount.maxUsers}
                          onChange={(e) => setDiscount(prev => ({ ...prev, maxUsers: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-md text-center font-semibold"
                        />
                      </div>
                    </div>
                  )}
                  
                  {discount.enabled && discount.percent && discount.maxUsers && (
                    <div className="bg-white rounded-lg p-3 border border-orange-300">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          <span className="font-semibold text-orange-600">
                            First {discount.maxUsers} buyers
                          </span>
                          <span> get </span>
                          <span className="font-semibold text-green-600">
                            {discount.percent}% OFF
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">Avg. savings</div>
                          <div className="font-bold text-green-600">
                            ₹{calculateDiscountValue().toFixed(0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Featured Products</label>
                <button 
                  onClick={() => setShowProductModal(true)}
                  className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-sm font-medium transition-colors flex items-center"
                >
                  <Plus size={14} className="mr-1" />
                  Add Product
                </button>
              </div>
              
              <div className="flex flex-wrap gap-2 mb-2">
                {featuredProducts.map(product => (
                  <div key={product.$id} className="flex items-center bg-white p-2 rounded-md border border-gray-300">
                    <span className="text-sm text-gray-700 mr-2">{product.title}</span>
                    <button 
                      onClick={() => removeProduct(product.$id)}
                      className="text-red hover:text-orange-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select from Existing Products
                </label>
                {availableProducts.length === 0 ? (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          No existing products found. Create your first product using the "Add Product" button above.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto">
                    {availableProducts.map(product => (
                      <div 
                        key={product.$id} 
                        className="flex items-center justify-between bg-white p-2 rounded-md border border-gray-300 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-center min-w-0">
                          <div className="w-8 h-8 bg-gray-200 rounded-md mr-2 flex-shrink-0">
                            {product.images[0] && (
                              <img 
                                src={product.images[0]} 
                                alt={product.title} 
                                className="w-full h-full object-cover rounded-md"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/assets/images/placeholder-product.png';
                                }}
                              />
                            )}
                          </div>
                          <span className="text-sm truncate">{product.title}</span>
                        </div>
                        <button 
                          onClick={() => addProduct(product)}
                          disabled={featuredProducts.some(p => p.$id === product.$id)}
                          className={`p-1 rounded-full ${
                            featuredProducts.some(p => p.$id === product.$id)
                              ? 'bg-green-100 text-green-600'
                              : 'bg-gray-100 hover:bg-blue-100 text-gray-600'
                          }`}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Important Delivery Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <Truck size={16} className="text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">
                    📦 Important: Delivery & Pricing
                  </h4>
                  <div className="space-y-2 text-sm text-blue-700">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                      <p>
                        <strong>You handle delivery:</strong> Make sure your product prices include packaging and shipping costs to your customers.
                      </p>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                      <p>
                        <strong>Pricing tip:</strong> Consider adding ₹50-100 to cover delivery expenses, or offer free shipping above a minimum order value.
                      </p>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Truck size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                      <p>
                        <strong>Delivery time:</strong> Clearly mention expected delivery time during your live stream to set proper customer expectations.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {showProductModal && (
              <ProductCreateModal
                sellerId={userId}
                onClose={() => setShowProductModal(false)}
                onProductCreated={handleProductCreated}
              />
            )}
            
            <div className="flex justify-end space-x-2">
              <button 
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded-md"
                onClick={() => setPreparing(false)}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 bg-red hover:bg-orange-500 text-white rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleStartStream}
                disabled={isLoading || !streamTitle.trim() || featuredProducts.length === 0}
              >
                {isLoading ? 'Starting...' : 'Go Live'} <Video size={16} className="ml-2" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12">
          <ShoppingBag size={48} className="text-gray-400 mb-4" />
          <h3 className="text-lg text-gray-400 font-medium mb-2">Create a Live Shopping Stream</h3>
          <p className="text-gray-500 text-center mb-4">Connect with your audience in real-time and showcase your products</p>
          <button 
            className="px-4 py-2 bg-red hover:bg-orange-600 text-white rounded-md"
            onClick={() => setPreparing(true)}
          >
            Create Stream
          </button>
        </div>
      )}
    </div>
  );
};

export { LiveStreamCreator };